import { convertAPI, urlToBase64 } from '@shareuc/share';
import { Assets, Container, Texture } from 'pixi.js';
import {
  ICommonComponentProps,
  IObjectMap,
  IPoint,
  TComponentLayer,
} from '../types';

export const addComponentEvents = (
  obj: Container,
  events: {
    pointerdown: () => void;
  },
) => {
  obj.eventMode = 'static';
  obj.on('pointerdown', () => {
    events.pointerdown();
  });
};

export const loadTexture = async (url: string) => {
  if (url && url.startsWith('http://')) {
    url = url.replace('http://', 'https://');
  }
  let newUrl = url;
  let texture = Texture.WHITE;
  if (newUrl) {
    let flag = false;
    if (newUrl.includes(convertAPI)) {
      flag = true;
    } else if (
      newUrl.includes('?x-oss-process') &&
      !newUrl.includes('.jpg?') &&
      !newUrl.includes('.jpeg?') &&
      !newUrl.includes('.png?') &&
      !newUrl.includes('.webp?')
    ) {
      flag = true;
    }
    if (flag) {
      newUrl = await urlToBase64(url);
    }
    texture = await Assets.load(newUrl);
  }
  return texture;
};

export const commonLoad = (obj: Container, props: ICommonComponentProps) => {
  obj.label = props.name;
  obj.zIndex = props.zIndex;
  obj.visible = props.visible;
  obj.angle = props.angle ?? 0;
  obj.alpha = props.alpha ?? 1;
};

export const commonUpdate = (
  obj: Container | null,
  props: ICommonComponentProps,
  objs: IObjectMap,
) => {
  if (!obj) return;
  obj.zIndex = props.zIndex;
  obj.visible = props.visible;
  obj.angle = props.angle ?? 0;
  //  mask
  if (props.mask) {
    setMask(obj, objs[props.mask]);
  } else {
    setMask(obj, null);
  }
  // parent
  const stage = objs[`stage_${props.layer}`];
  if (props.parent) {
    if (obj.parent !== objs[props.parent]) {
      if (objs[props.parent]) {
        setParent(obj, objs[props.parent]);
      } else {
        setParent(obj, stage);
      }
    }
  } else {
    setParent(obj, stage);
  }
};

const setParent = (obj: Container, parent: Container | null) => {
  if (!parent) return;
  if (obj.parent !== parent) {
    parent.addChild(obj);
  }
};

const setMask = (obj: Container, mask: Container | null) => {
  if (obj.mask !== mask) {
    obj.mask = mask;
  }
};

export const getAppZIndex = (layer: TComponentLayer) => {
  switch (layer) {
    case 'bottom':
      return '0';
    case 'center':
      return '1';
    case 'top':
      return '2';
    default:
      return '0';
  }
};

interface IGetPolygonProps {
  count: number;
  radius: number;
  center: { x: number; y: number };
  angle: number;
}

export const getPolygon = (props: IGetPolygonProps) => {
  const { count, center, radius, angle } = props;
  const polygon: number[] = [];
  for (let i = 0; i < count; i++) {
    const radian = (2 * Math.PI) / count;
    const x1 =
      center.x + radius * Math.cos(radian * i + (Math.PI / 180) * angle);
    const y1 =
      center.y + radius * Math.sin(radian * i + (Math.PI / 180) * angle);
    polygon.push(x1, y1);
  }
  return polygon;
};

export const rotatePoint = (
  center: { x: number; y: number },
  point: { x: number; y: number },
  angle: number,
) => {
  const rotation = -(Math.PI / 180) * angle;
  const x =
    center.x +
    (point.x - center.x) * Math.cos(rotation) -
    (point.y - center.y) * Math.sin(rotation);
  const y =
    center.y +
    (point.x - center.x) * Math.sin(rotation) +
    (point.y - center.y) * Math.cos(rotation);
  return { x, y };
};

// 获取点落在多边形的区域的序号
export const getAreaIndex = (point: IPoint, center: IPoint, ks: number) => {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const radians = Math.atan2(dy, dx);
  let degrees = radians * (180 / Math.PI);
  degrees = degrees - 90; // 调整基准到正下方并顺时针
  degrees = ((degrees % 360) + 360) % 360;
  const segmentAngle = 360 / ks;
  return Math.floor(degrees / segmentAngle);
};
